// B-89 — the pure half of each office's own email: reading the offices' addresses out of an Excel,
// checking them against the client's offices before anything is saved, and saying how a send went.
//
// The backend keeps the addresses per office (`saveCierreOfficeContacts`, all or nothing) and sends
// each office its own report (`sendCierreOfficeReport`). An office with no address is skipped and
// said — never the reason the others do not go.

import { correctionError } from "./cierreCorrections";
import { correosEscritos } from "./cierreConsolidado";

const CODIGO = /c[oó]d/i;
const CORREO_TITULO = /correo|e-?mail|mail/i;

const texto = (celda) => {
  if (celda === null || celda === undefined) return "";
  // An office code typed as a number comes back as one (227), and String() writes it as typed.
  return String(celda).trim();
};

// The rows of the sheet (`sheet_to_json` with `header: 1`) -> each office with its addresses.
//
// The sheet may carry a heading row — a column whose title says "código" and the ones that say
// "correo"/"email" — or none, and then the first column is the office and every other cell holds
// addresses. Several addresses in one cell (commas, semicolons, spaces or lines) and an office
// repeated on several rows are both fine: an office's addresses are all of them, once.
export function oficinasDeExcel(filas) {
  const todas = (filas || []).map((fila) => (Array.isArray(fila) ? fila : []));
  const inicio = todas.findIndex((fila) => fila.some((celda) => CODIGO.test(texto(celda))));
  let columnaCodigo = 0;
  let columnasCorreo = null;
  let desde = 0;
  if (inicio >= 0) {
    const titulos = todas[inicio].map(texto);
    columnaCodigo = titulos.findIndex((t) => CODIGO.test(t));
    const marcadas = titulos.map((t, i) => (i !== columnaCodigo && CORREO_TITULO.test(t) ? i : -1)).filter((i) => i >= 0);
    columnasCorreo = marcadas.length ? marcadas : null;
    desde = inicio + 1;
  }

  const porOficina = new Map();
  todas.slice(desde).forEach((fila) => {
    const codigo = texto(fila[columnaCodigo]);
    if (!codigo) return;
    const celdas = columnasCorreo
      ? columnasCorreo.map((i) => fila[i])
      : fila.filter((_, i) => i !== columnaCodigo);
    const { correos, invalidos } = correosEscritos(celdas.map(texto).join(" "));
    const previa = porOficina.get(codigo) || { codigo, correos: [], invalidos: [] };
    porOficina.set(codigo, {
      codigo,
      correos: [...new Set([...previa.correos, ...correos])],
      invalidos: [...new Set([...previa.invalidos, ...invalidos])],
    });
  });
  return [...porOficina.values()];
}

// What was read, against the client's offices as `listCierreOfficeContacts` answered them: each
// office "lista" to save, or why not — unknown here, outside the person's permission, or carrying
// something that is not an address. The save is all or nothing on the server too, so the screen
// only offers it when every row is ready.
export function revisarCarga(leidas, oficinas) {
  const conocidas = new Map((oficinas || []).map((o) => [String(o.officeCode), o]));
  const filas = (leidas || []).map((leida) => {
    const oficina = conocidas.get(leida.codigo);
    let problema = null;
    if (!oficina) problema = "No es una oficina de este cliente";
    else if (!oficina.editable) problema = "No tienes permiso para cambiar sus correos";
    else if (leida.invalidos.length) problema = `No es un correo válido: ${leida.invalidos.join(", ")}`;
    else if (!leida.correos.length) problema = "Sin correos";
    return {
      codigo: leida.codigo,
      nombre: oficina ? oficina.officeName : null,
      correos: leida.correos,
      antes: oficina ? oficina.emails || [] : [],
      problema,
    };
  });
  return {
    filas,
    listas: filas.filter((f) => !f.problema),
    puedeGuardar: filas.length > 0 && filas.every((f) => !f.problema),
  };
}

export const ESTADO_ENVIO = {
  SENT: "Enviado",
  NO_RECIPIENTS: "Sin correos",
  FAILED: "No se pudo enviar",
  PREVIEW: "Listo para enviar",
};

const oficinas = (n) => (n === 1 ? "1 oficina" : `${n} oficinas`);

// How a send went, in the operator's words: `{ ok, texto, oficinas }`. A group where some offices
// had no address is still a success for the ones that went — the others are named so they can be
// given one in Oficinas.
export function resultadoDeEnvioOficinas(response) {
  if (!response?.success) {
    return {
      ok: false,
      texto: response?.errorCode || response?.message ? correctionError(response) : "No se pudo enviar. Inténtalo de nuevo.",
      oficinas: [],
    };
  }
  const { offices = [], sent = 0, skipped = 0, failed = 0 } = response.data || {};
  const partes = [];
  if (sent) partes.push(`Enviado a ${oficinas(sent)}.`);
  if (skipped) {
    const sin = offices.filter((o) => o.status === "NO_RECIPIENTS").map((o) => `${o.officeName} (${o.officeCode})`);
    partes.push(`${oficinas(skipped)} sin correos: ${sin.join(", ")}.`);
  }
  if (failed) partes.push(`${oficinas(failed)} no se pudo enviar.`);
  return { ok: sent > 0 && !failed, texto: partes.join(" ") || "No se envió nada.", oficinas: offices };
}
